/**
 * storm_hazards.ts — STORM HAZARDS that never kill (2026-09-24; research §4.6: "a mild effect — slower walk, a HUD
 * warning — under the non-punitive law; never damage"). PURE rules + one globalThis slot the avatar reads.
 *
 *   THE HAZARDS   a thunderstorm (rain above STORM_DENSITY), a blizzard (snow above BLIZZARD_DENSITY, or cold snow
 *                 in a gale), a sandstorm (the weather's dust above SAND_DUST). Out in one, EXPOSURE rises (full in
 *                 about a minute); in shelter — any interior, a cave, underwater, a vehicle's cabin — or by a fire it
 *                 falls, faster by the fire. Calm weather lets it ebb.
 *   THE EFFECTS   exposure SLOWS you (the walk intent scaled down to a floor of STORM_SPEED_FLOOR — THE INTENT-SCALE
 *                 LAW: the server integrates the shortened intent the same way, so nothing desyncs); a blizzard CHILLS
 *                 (no sprinting while chilled); a sandstorm BLINDS (a vignette the HUD draws); a thunderstorm's
 *                 lightning can land NEAR you (a stagger, a scorch on the ground, the crack at once) — never on you.
 *   NEVER DAMAGE  nothing here writes a health row or calls a reducer; a hazard is weather you feel, not a death.
 */

export type HazardKind = 'storm' | 'blizzard' | 'sandstorm' | '';

export const STORM_DENSITY = 0.8;
export const BLIZZARD_DENSITY = 0.65;
export const SAND_DUST = 0.35;
/** Seconds of open exposure to reach 1, and to shed it sheltered / by a fire / in calm air. */
export const EXPOSE_RISE_S = 60;
export const SHELTER_FALL_S = 20;
export const FIRE_FALL_S = 8;
export const CALM_FALL_S = 30;
/** The slowest a hazard makes you walk (×intent). */
export const STORM_SPEED_FLOOR = 0.7;
/** A blizzard chills past this exposure (no sprint). */
export const CHILL_AT = 0.6;
/** Lightning: a near miss at most this often per exposed second at full exposure, at this distance band. */
export const NEAR_MISS_PER_S = 1 / 40;
export const NEAR_MISS_MIN_M = 18;
export const NEAR_MISS_MAX_M = 34;
export const STAGGER_S = 0.7;
export const STAGGER_SCALE = 0.35;

export interface HazardInput {
  /** The weather's precipitation kind ('rain' | 'snow' | 'dust' | '') and its density 0..1. */
  kind: string;
  density: number;
  /** Wind strength 0..1. */
  wind: number;
  /** The weather's dust 0..1 (sandstorms). */
  dust: number;
  temperatureC: number | null;
  /** Under a roof, in a cave, underwater, in a ship's cabin, in space. */
  sheltered: boolean;
  /** Within reach of a fire (a camp, a town's fire circle). */
  nearFire: boolean;
}

/** Which hazard (if any) the weather is right now. */
export function hazardKind(i: HazardInput): HazardKind {
  if (i.dust >= SAND_DUST || (i.kind === 'dust' && i.density >= SAND_DUST)) return 'sandstorm';
  if (i.kind === 'snow' && (i.density >= BLIZZARD_DENSITY || (i.density >= 0.4 && i.wind >= 0.7))) return 'blizzard';
  if (i.kind === 'rain' && i.density >= STORM_DENSITY) return 'storm';
  return '';
}

/** How hard the hazard bites, 0..1 (its own weather's strength above its threshold). */
export function severity(i: HazardInput): number {
  const k = hazardKind(i);
  if (k === 'sandstorm') return Math.min(1, 0.55 + Math.max(i.dust, i.kind === 'dust' ? i.density : 0) * 0.45);
  if (k === 'blizzard') return Math.min(1, 0.55 + i.density * 0.3 + i.wind * 0.15 + (i.temperatureC !== null && i.temperatureC < -5 ? 0.1 : 0));
  if (k === 'storm') return Math.min(1, 0.5 + (i.density - STORM_DENSITY) * 1.5 + i.wind * 0.2);
  return 0;
}

/** One step of exposure (0..1) over `dt` seconds. */
export function stepExposure(prev: number, i: HazardInput, dt: number): number {
  const d = Math.max(0, Math.min(1, dt));
  const k = hazardKind(i);
  let e = prev;
  if (i.nearFire) e -= d / FIRE_FALL_S;
  else if (i.sheltered) e -= d / SHELTER_FALL_S;
  else if (k) e += (d / EXPOSE_RISE_S) * (0.6 + 0.4 * severity(i));
  else e -= d / CALM_FALL_S;
  return Math.max(0, Math.min(1, e));
}

/** The intent scale exposure applies (1 = none). Sheltered or calm, exposure has ebbed and this returns toward 1. */
export function speedScale(exposure: number, sev: number): number {
  const s = 1 - (1 - STORM_SPEED_FLOOR) * Math.max(0, Math.min(1, exposure)) * Math.max(0.5, Math.min(1, sev || 0.5));
  return Math.max(STORM_SPEED_FLOOR, Math.min(1, s));
}

/** Chilled (no sprint) — a blizzard past CHILL_AT. */
export function chilled(kind: HazardKind, exposure: number): boolean {
  return kind === 'blizzard' && exposure >= CHILL_AT;
}

/** The sandstorm's vignette strength 0..1 (the HUD's canvas). */
export function sandBlind(kind: HazardKind, exposure: number): number {
  return kind === 'sandstorm' ? Math.min(0.85, exposure * 0.85) : 0;
}

/** Does a lightning near-miss land this step? `roll` is a uniform 0..1. Never while sheltered. */
export function nearMissRoll(kind: HazardKind, exposure: number, sheltered: boolean, dt: number, roll: number): boolean {
  if (kind !== 'storm' || sheltered || exposure < 0.25) return false;
  return roll < NEAR_MISS_PER_S * exposure * Math.max(0, Math.min(1, dt));
}

/** Where a near miss lands: a bearing and a distance in the band (from two uniforms). */
export function nearMissSpot(x: number, z: number, u1: number, u2: number): { x: number; z: number; dist: number } {
  const a = u1 * Math.PI * 2;
  const dist = NEAR_MISS_MIN_M + u2 * (NEAR_MISS_MAX_M - NEAR_MISS_MIN_M);
  return { x: x + Math.cos(a) * dist, z: z + Math.sin(a) * dist, dist };
}

/** The HUD's line for a hazard. */
export function hazardLabel(kind: HazardKind): string {
  return kind === 'storm' ? 'Thunderstorm' : kind === 'blizzard' ? 'Blizzard' : kind === 'sandstorm' ? 'Sandstorm' : '';
}

// ── the shared slot (the avatar reads it every fixed tick) ────────────────────
interface Slot { scale: number; chilled: boolean; staggerUntil: number }
function slot(): Slot {
  const g = globalThis as unknown as { __atlasStormHazard?: Slot };
  if (!g.__atlasStormHazard) g.__atlasStormHazard = { scale: 1, chilled: false, staggerUntil: 0 };
  return g.__atlasStormHazard;
}
export function setStormEffect(scale: number, isChilled: boolean): void { const s = slot(); s.scale = scale; s.chilled = isChilled; }
export function staggerFor(ms: number, nowMs: number): void { slot().staggerUntil = nowMs + ms; }
/** The intent multiplier the avatar applies (the stagger of a near miss wins while it lasts). */
export function stormSpeedScale(nowMs: number): number {
  const s = slot();
  return nowMs < s.staggerUntil ? Math.min(s.scale, STAGGER_SCALE) : s.scale;
}
export function stormChilled(): boolean { return slot().chilled; }
